/**
 * PB-Detail · Bewertungen
 *
 * Two stacked windows:
 *   1. Erhaltene Bewertungen — Ratings other people gave this PB
 *      (loaded with user_role of the OTHER side; controller flips the
 *      role server-side so we pass `user.user_role`).
 *   2. Geschriebene Bewertungen — Ratings this PB authored about BKs
 *
 * Daten kommen aus den v2-Endpunkten (/v2/admin/users/{id}/ratings/...), die
 * die Rolle selbst auswerten und Namen aufloesen. Die bewertende Person ist
 * ein verknuepftes Profil ODER ein Freitext-Name; angezeigt wird in beiden
 * Faellen nur der Name (kein Link).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import { v1Users, type V1User, type V2RatingRow } from '../../../lib/v1';
import { Rating } from '../profile/controls';
import { formatDate } from '../../../lib/dateFormat';
import { RatingCreateDrawer } from './RatingCreateDrawer';
import { criteriaForRow } from './ratingCriteria';

interface Props {
  user: V1User | null;
  dark: boolean;
}

/** Einzelkriterien liegen je nach Tabelle (ratings / ratings_bk) in den Zeilenfeldern, siehe ratingCriteria.ts. */
type RatingRow = V2RatingRow;

export function BewertungenTab({ user, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [received, setReceived] = useState<RatingRow[]>([]);
  const [created, setCreated] = useState<RatingRow[]>([]);
  const [loadingR, setLoadingR] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [errorR, setErrorR] = useState<string | null>(null);
  const [errorC, setErrorC] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [reload, setReload] = useState(0);
  // Bewertungen hinzufügen: beim PB (bewertet von BK) und bei der BK (bewertet
  // vom Kunden/PB), jeweils mit verknüpftem Profil ODER Freitext-Name.
  const ratedRole = Number(user?.user_role) === 1 ? 'BK' : 'PB';
  const canCreate = user?.id != null && [1, 2].includes(Number(user.user_role));

  useEffect(() => {
    if (!user?.id) { setLoadingR(false); setLoadingC(false); return; }
    let alive = true;

    setLoadingR(true); setErrorR(null);
    v1Users.ratingsList(user.id, 'received')
      .then(rows => { if (alive) setReceived(rows); })
      .catch(e => { if (alive) setErrorR(e instanceof Error ? e.message : tr('pflege.ratings.error_received')); })
      .finally(() => { if (alive) setLoadingR(false); });

    setLoadingC(true); setErrorC(null);
    v1Users.ratingsList(user.id, 'created')
      .then(rows => { if (alive) setCreated(rows); })
      .catch(e => { if (alive) setErrorC(e instanceof Error ? e.message : tr('pflege.ratings.error_written')); })
      .finally(() => { if (alive) setLoadingC(false); });

    return () => { alive = false; };
  }, [user?.id, user?.user_role, reload]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}>

      <RatingsWindow
        dark={dark}
        title={tr('pflege.ratings.received_title')}
        rows={received}
        loading={loadingR}
        error={errorR}
        emptyHint={tr('pflege.ratings.empty_received')}
        action={canCreate ? (
          <Button variant="primary" size="sm" dark={dark} onClick={() => setCreateOpen(true)}>
            {tr('pflege.ratings.create.button')}
          </Button>
        ) : undefined}
      />
      {canCreate && (
        <RatingCreateDrawer
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          pbUserId={user!.id}
          ratedRole={ratedRole}
          dark={dark}
          onCreated={() => setReload(x => x + 1)}
        />
      )}

      <RatingsWindow
        dark={dark}
        title={tr('pflege.ratings.written_title')}
        rows={created}
        loading={loadingC}
        error={errorC}
        emptyHint={tr('pflege.ratings.empty_written')}
        nameField="fuer_name"
      />

    </div>
  );
}

function RatingsWindow({
  title, rows, loading, error, emptyHint, dark, action, nameField = 'von_name',
}: {
  /** Welcher aufgeloeste Name pro Zeile gezeigt wird: bewertende (erhalten) oder bewertete Person (geschrieben). */
  nameField?: 'von_name' | 'fuer_name';
  title: string;
  /** Optionale Aktion rechts im Kopf (z. B. „Bewertung hinzufügen"). */
  action?: React.ReactNode;
  rows: RatingRow[];
  loading: boolean;
  error: string | null;
  emptyHint: string;
  dark: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  return (
    <Card dark={dark} style={{ padding: 0 }}>
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>
          {rows.length} {title}
        </h3>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {loading && <span style={{ fontSize: 12, color: t.muted }}>{tr('common.loading_short')}</span>}
          {action}
        </span>
      </div>
      {error ? (
        <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{error}</div>
      ) : (
        <div>
          {!loading && rows.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>
              {emptyHint}
            </div>
          )}
          {rows.map(r => <RatingRowCard key={r.id} r={r} dark={dark} nameField={nameField} />)}
        </div>
      )}
    </Card>
  );
}

function RatingRowCard({ r, dark, nameField }: { r: RatingRow; dark: boolean; nameField: 'von_name' | 'fuer_name' }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const criteria = criteriaForRow(r).filter(c => Number(r[c.field]) > 0);
  return (
    <div style={{
      padding: '14px 20px', borderBottom: `1px solid ${t.borderSoft}`,
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, alignItems: 'start',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>
          {r[nameField] ?? tr('pflege.ratings.user_fallback', { id: (nameField === 'von_name' ? r.von_user_id : r.user_id) || '-' })}
        </div>
        <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>
          {fmtDate(r.created_at)}
          {r.care_id ? <> · {tr('pflege.ratings.care_ref', { id: r.care_id })}</> : null}
        </div>
        {criteria.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6, fontSize: 12, color: t.muted }}>
            {criteria.map(c => (
              <span key={c.key}>
                {tr(`pflege.ratings.create.criteria.${c.key}`)}: <span style={{ color: '#f5a623' }}>{'★'.repeat(Number(r[c.field]))}</span>
              </span>
            ))}
          </div>
        )}
        {r.rating_text ? (
          <div style={{ fontSize: 13, color: t.text, marginTop: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {r.rating_text}
          </div>
        ) : null}
      </div>
      <Rating dark={dark} value={Number(r.rating_gesamt ?? 0)} />
    </div>
  );
}

function fmtDate(v: unknown): string {
  if (v == null || v === '') return '-';
  return formatDate(String(v), String(v));
}
